import * as React from 'react'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import { ListItemIcon, ListSubheader, Grid } from '@mui/material'
import DeleteScenario from './DeleteScenario'
import EditScenario from './EditScenario'
import ScenarioIcon from '@mui/icons-material/Tune'
import { JSONSchema7 } from 'json-schema'
import CreateScenario from './CreateScenario'
import { Simulation } from '../../interfaces/simulation'
import { Battery } from '../../interfaces/battery'
import { PvInstallation } from '../../interfaces/pvinstallation'
import ChartIcon from '@mui/icons-material/Addchart'
import Button from '@mui/material/Button/Button'
import Typography from '@mui/material/Typography/Typography'
import DialogActions from '@mui/material/DialogActions/DialogActions'
import ButtonDialog from '../shared/ButtonDialog'
import DownloadIcon from '@mui/icons-material/SaveAlt'
import axios from 'axios'
import Select from '@mui/material/Select/Select'
import InputLabel from '@mui/material/InputLabel/InputLabel'
import MenuItem from '@mui/material/MenuItem/MenuItem'
import FormControl from '@mui/material/FormControl/FormControl'

export interface Props {
  simulation: Simulation
  disabled: boolean
  data: any[]
  setData: React.Dispatch<React.SetStateAction<any[]>>
  scenario: any
  setScenario: React.Dispatch<React.SetStateAction<any[]>>
}

export default function ScenarioList({ simulation, disabled, data, setData, scenario, setScenario }: Props) {
  const [openResults, setOpenResults] = React.useState<boolean>(false)
  const [openDownload, setOpenDownload] = React.useState<boolean>(false)
  const [name, setName] = React.useState<any>(undefined)
  const [obj, setObj] = React.useState<any>(undefined)
  const [idx, setIdx] = React.useState<any>(undefined)

  const getDialogContentResults = () => {
    switch (simulation?.status?.progress) {
      case 100:
        return (
          <>
            <Typography variant='body2'>
              If you encounter errors when fetching results, this might indicate you have changed the config without
              re-running the scenario.
            </Typography>
            <br />
            <Typography variant='body2'>After running the scenario again, these errors should disappear.</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenResults(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setOpenResults(false)
                  setScenario(name)
                }}
              >
                Fetch
              </Button>
            </DialogActions>
          </>
        )
      case 0:
        return (
          <>
            <Typography variant='body2'>In order to view results, you need to run the simulation first.</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenResults(false)}>Ok</Button>
            </DialogActions>
          </>
        )
      default:
        return (
          <>
            <Typography variant='body2' color='error'>
              {`Errors were encountered when running the scenario, only ${
                Math.round(simulation?.status.progress * 100.0) / 100
              }% of results will be available.`}
            </Typography>

            <br />
            <Typography variant='body2'>Are you sure you want to fetch these results?</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenResults(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setOpenResults(false)
                  setScenario(name)
                }}
              >
                Fetch
              </Button>
            </DialogActions>
          </>
        )
    }
  }

  const getDialogContentDownload = () => {
    if (!obj) return <></>
    switch (simulation?.status?.progress) {
      case 100:
        return (
          <>
            <Typography variant='body2'>
              If you encounter errors when downloading results, this might indicate you have changed the config without
              re-running the scenario.
            </Typography>
            <FormControl size='small' sx={{ width: '100%', mt: 2.5 }}>
              <InputLabel>Index</InputLabel>
              <Select
                value={idx}
                label='Index'
                onChange={(e) => {
                  setIdx(e.target.value)
                }}
              >
                {(Object.values(obj.config)[0] as any).map((value: any, index: any) => {
                  return (
                    <MenuItem key={index} value={index}>
                      {index}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenDownload(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  setOpenDownload(false)
                  try {
                    const res = await axios.get(
                      `${process.env.REACT_APP_API}/${simulation.simulation_name}/results/download/${obj.name}/${idx}`,
                      {
                        withCredentials: true,
                        responseType: 'blob',
                      }
                    )
                    const url = window.URL.createObjectURL(new Blob([res.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', obj.name + '_' + idx + '.zip')
                    document.body.appendChild(link)
                    link.click()
                  } catch (err) {
                    console.log('err:', err)
                  }
                }}
              >
                Download
              </Button>
            </DialogActions>
          </>
        )
      case 0:
        return (
          <>
            <Typography variant='body2'>In order to download results, you need to run the scenario first.</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenResults(false)}>Ok</Button>
            </DialogActions>
          </>
        )
      default:
        return (
          <>
            <Typography variant='body2' color='error'>
              {`Errors were encountered when running the scenario, only ${
                Math.round(simulation?.status.progress * 100.0) / 100
              }% of results will be available.`}
            </Typography>
            <br />
            <Typography variant='body2'>Are you sure you want to download these results?</Typography>
            <FormControl size='small' sx={{ width: '100%', mt: 2.5 }}>
              <InputLabel>Index</InputLabel>
              <Select
                value={idx}
                label='Index'
                onChange={(e) => {
                  setIdx(e.target.value)
                }}
              >
                {(Object.values(obj.config)[0] as any).map((value: any, index: any) => {
                  return (
                    <MenuItem key={index} value={index}>
                      {index}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenDownload(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  setOpenDownload(false)
                  try {
                    const res = await axios.get(
                      `${process.env.REACT_APP_API}/${simulation.simulation_name}/results/download/${obj.name}/${idx}`,
                      {
                        withCredentials: true,
                        responseType: 'blob',
                      }
                    )
                    const url = window.URL.createObjectURL(new Blob([res.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', obj.name + '_' + idx + '.' + res.data.type.split('/')[1])
                    document.body.appendChild(link)
                    link.click()
                  } catch (err) {
                    console.log('err:', err)
                  }
                }}
              >
                Download
              </Button>
            </DialogActions>
          </>
        )
    }
  }

  const schema = {
    name: 'scenario',
    type: 'object',
    properties: {
      name: {
        type: 'string',
        pattern: '^[\\w\\-]+$',
        description: 'Allowed characters: [0-9a-zA-Z_]',
      },
      config: {
        type: 'object',
        properties: {},
      },
    },
    required: ['name'],
  }

  if (simulation.battery) {
    Object.keys(simulation.battery as Battery).forEach((key: string) => {
      Object.assign(schema.properties.config.properties, {
        [key]: {
          type: 'array',
          minItems: 1,
          maxItems: 5,
          description: 'Maximum storage capacity [Wh]',
          items: {
            type: 'number',
            default: 240000,
          },
        },
      })
    })
  }

  if (simulation.pvinstallation) {
    Object.keys(simulation.pvinstallation as PvInstallation).forEach((key: string) => {
      Object.assign(schema.properties.config.properties, {
        [key]: {
          type: 'array',
          minItems: 1,
          maxItems: 5,
          description: 'Installed capacity [W]',
          items: {
            type: 'number',
            default: 100000,
          },
        },
      })
    })
  }

  return (
    <List
      dense
      subheader={
        <ListSubheader>
          <Grid container alignItems={'center'}>
            {'Scenario'}
            <CreateScenario disabled={disabled} data={data} schema={schema as JSONSchema7} setData={setData} />
          </Grid>
        </ListSubheader>
      }
    >
      {data.map((obj: any, index: number) => {
        return (
          <ListItem
            key={index}
            secondaryAction={
              <>
                <ButtonDialog
                  disabled={simulation?.status?.running}
                  title={`Download Results`}
                  icon={<DownloadIcon />}
                  iconButton={<DownloadIcon />}
                  content={getDialogContentDownload()}
                  open={openDownload}
                  setOpen={(bool) => {
                    setOpenDownload(bool)
                    setIdx(0)
                    setObj(obj)
                  }}
                />
                <ButtonDialog
                  disabled={simulation?.status?.running}
                  title={`Fetch Results`}
                  icon={<ChartIcon />}
                  iconButton={<ChartIcon />}
                  content={getDialogContentResults()}
                  open={openResults}
                  setOpen={(bool) => {
                    setOpenResults(bool)
                    setName(obj.name)
                  }}
                />
                <EditScenario disabled={disabled} schema={schema as JSONSchema7} value={obj} setData={setData} />
                <DeleteScenario disabled={disabled} value={obj} setData={setData} />
              </>
            }
            disablePadding
          >
            <ListItemButton selected={scenario === obj.name} disabled={disabled}>
              <ListItemIcon>
                <ScenarioIcon />
              </ListItemIcon>
              <ListItemText
                primary={obj.name}
                secondary={Object.entries(obj.config).map((b) => {
                  const name = b[0]
                  const value = b[1] as object
                  if (name !== 'name') return `${name}: [${value}] `
                  else return ''
                })}
                sx={{
                  '.MuiListItemText-secondary': { display: { xs: 'none', sm: 'block' } },
                }}
              />
            </ListItemButton>
          </ListItem>
        )
      })}
    </List>
  )
}
